import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase-config';
import { useAuth } from '../contexts/AuthContext';
import { REPORTS_DISCLAIMER_PLAIN_ENGLISH_SUMMARY } from '../data/legalDisclaimers';
import { getAttributionContext } from '../utils/utm';
import { trackAssessmentToPricing } from '../utils/marketingEvents';

const AssessmentResults = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState('');

  useEffect(() => {
    fetchAssessment();
  }, [token]);

  const fetchAssessment = async () => {
    try {
      // Try to fetch token data directly first
      let assessmentData = null;
      
      try {
        const tokenDoc = await getDoc(doc(db, 'publicTokens', token));
        
        if (!tokenDoc.exists()) {
          throw new Error('Token not found');
        }

        const tokenData = tokenDoc.data();
        
        // Check if assessment data is embedded in the token (new approach)
        if (tokenData.assessmentData) {
          assessmentData = tokenData.assessmentData;
        } else if (tokenData.assessmentId) {
          // Try to get assessment directly (for old tokens)
          // This will only work for authenticated users
          try {
            const assessmentRef = doc(db, 'assessments', tokenData.assessmentId);
            const assessmentDoc = await getDoc(assessmentRef);
            
            if (assessmentDoc.exists()) {
              assessmentData = assessmentDoc.data();
            }
          } catch (err) {
            console.log('Could not fetch from assessments collection, will try Cloud Function');
          }
        }
      } catch (firestoreErr) {
        console.log('Direct Firestore access failed:', firestoreErr.message);
        console.log('Attempting fallback via Cloud Function...');
      }
      
      // If we couldn't get data from Firestore, try the Cloud Function fallback
      if (!assessmentData) {
        try {
          // First try the rewrite path (no CORS!)
          let response = await fetch(`/api/getPublicAssessment?token=${token}`);
          
          // If rewrite fails, fallback to direct URL
          if (!response.ok && response.status === 404) {
            console.log('Rewrite not available, using direct URL fallback');
            const apiUrl = import.meta.env.VITE_API_URL || 'https://us-central1-adu-lab-uat.cloudfunctions.net';
            response = await fetch(`${apiUrl}/getPublicAssessment?token=${token}`);
          }
          
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to fetch assessment');
          }
          
          const result = await response.json();
          if (result.success && result.data) {
            assessmentData = result.data;
            console.log(`Assessment loaded via Cloud Function (source: ${result.source})`);
          } else {
            throw new Error(result.error || 'No assessment data returned');
          }
        } catch (functionErr) {
          console.error('Cloud Function fallback failed:', functionErr);
          setError('Assessment not found or expired');
          setLoading(false);
          return;
        }
      }
      
      // Set the assessment data if we got it
      if (assessmentData) {
        setAssessment(assessmentData);
      } else {
        setError('Assessment data not found');
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching assessment:', err);
      console.error('Error details:', err.message, err.code);
      setError('Failed to load assessment');
      setLoading(false);
    }
  };

  const getGradeInfo = (score) => {
    if (score >= 93) return { grade: 'A+', class: 'pulse-a-plus' };
    if (score >= 85) return { grade: 'A', class: 'pulse-a' };
    if (score >= 80) return { grade: 'B+', class: 'pulse-b-plus' };
    if (score >= 70) return { grade: 'B', class: 'pulse-b' };
    if (score >= 60) return { grade: 'C', class: 'pulse-c' };
    if (score >= 50) return { grade: 'D', class: 'pulse-d' };
    return { grade: 'F', class: 'pulse-f' };
  };

  const handleUpgrade = () => {
    const attributionContext = getAttributionContext();

    trackAssessmentToPricing({
      ...attributionContext,
      assessmentId: assessment?.id,
      publicToken: token,
      ctaId: 'assessment_results_upgrade',
      cta_destination: '/pricing',
    });

    // Navigate to pricing with email and assessment data
    navigate('/pricing', { 
      state: { 
        email: assessment?.answers?.email || '',
        assessmentId: assessment?.id,
        publicToken: token,
        fromAssessment: true
      } 
    });
  };

  // Don't show a loader - we already showed one in the Assessment page
  if (loading) {
    return null; // Return nothing while loading to avoid double loader
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-6 py-2 rounded-full hover:bg-blue-700"
          >
            Start New Assessment
          </button>
        </div>
      </div>
    );
  }

  const { aiAnalysis, answers = {}, reportDate, expiryDate, id } = assessment;
  const sparScoreAi = aiAnalysis?.sparScoreAi || 75;
  const letterGrade = aiAnalysis?.letterGrade || 'B';
  
  // Check if this is test/fallback data
  const isTestData = !aiAnalysis?.sparScoreAi || aiAnalysis?.isTestData || 
                     (aiAnalysis?.summary && aiAnalysis.summary.includes('Based on your') && 
                      aiAnalysis.summary.includes('property in'));
  
  // Get the pulse class based on letter grade
  const getPulseClass = (grade) => {
    switch(grade) {
      case 'A+': return 'pulse-a-plus';
      case 'A': return 'pulse-a';
      case 'B+': return 'pulse-b-plus';
      case 'B': return 'pulse-b';
      case 'C': return 'pulse-c';
      case 'D': return 'pulse-d';
      case 'F': return 'pulse-f';
      default: return 'pulse-b';
    }
  };
  
  const gradeInfo = {
    grade: letterGrade,
    class: getPulseClass(letterGrade)
  };
  
  // Debug AI analysis
  
  

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&family=Google+Sans:wght@400;500;700&family=Montserrat:wght@600&display=swap');
        @import url('https://fonts.googleapis.com/icon?family=Material+Icons');
        
        :root {
            --bg-color: #f8f9fa;
            --surface-color: #ffffff;
            --text-primary: #202124;
            --text-secondary: #5f6368;
            --border-color: #dadce0;
            --accent-primary: #1a73e8;
            --accent-active-bg: #e8f0fe;
            --accent-yellow: #fbbc04;
            --success-green: #10B981;
            --warning-yellow: #F59E0B;
            --danger-red: #EF4444;
            --font-sans: 'Google Sans', 'Roboto', sans-serif;
            --font-display: 'Google Sans', sans-serif;
            --font-brand: 'Montserrat', sans-serif;
        }
        
        .assessment-results *, .assessment-results *::before, .assessment-results *::after { box-sizing: border-box; }
        
        .assessment-results {
            font-family: var(--font-sans);
            background-color: var(--bg-color);
            color: var(--text-primary);
            -webkit-font-smoothing: antialiased;
        }
        
        .container {
            max-width: 900px;
            margin: 0 auto;
            padding: 40px 20px;
        }

        /* Header */
        .report-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding-bottom: 20px;
            margin-bottom: 40px;
        }
        
        .header-left, .header-right {
            display: flex;
            gap: 12px;
        }
        
        .btn {
            padding: 10px 24px;
            border: 1px solid var(--border-color);
            background: transparent;
            color: var(--accent-primary);
            border-radius: 999px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
            font-size: 14px;
            text-decoration: none;
            display: inline-flex;
            align-items: center;
            gap: 8px;
            white-space: nowrap;
        }
        
        .btn:hover { 
            background-color: var(--accent-active-bg); 
        }
        
        .btn.primary { 
            background: var(--accent-primary); 
            color: white; 
            border-color: var(--accent-primary); 
        }
        
        .btn.primary:hover { 
            box-shadow: 0 1px 2px 0 rgba(60,64,67,.3), 0 1px 3px 1px rgba(60,64,67,.15); 
        }

        /* Report Title */
        .title-section {
            text-align: center;
            margin-bottom: 40px;
        }
        
        .title-section h1 {
            font-family: var(--font-display);
            font-size: 36px;
            font-weight: 500;
            margin-bottom: 8px;
        }
        
        .title-section p {
            font-size: 16px;
            color: var(--text-secondary);
        }

        /* Card Styles */
        .card {
            background: var(--surface-color);
            border: 1px solid var(--border-color);
            border-radius: 16px;
            padding: 32px;
            margin-bottom: 24px;
        }
        .card-title-container {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 16px;
        }
        .card-title {
            font-family: var(--font-display);
            font-size: 20px;
            font-weight: 500;
            margin: 0;
        }
        .ai-disclosure-icon {
            color: var(--accent-primary);
            cursor: pointer;
            display: flex;
            align-items: center;
        }
        
        .ai-indicator {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            color: var(--accent-primary);
            font-size: 12px;
            font-weight: 500;
            margin-left: 8px;
            cursor: pointer;
        }
        
        .ai-indicator:hover {
            opacity: 0.8;
        }
        
        .ai-indicator .material-icons {
            font-size: 16px;
        }

        /* SPAR SCALE CARD WITH PULSE BAR */
        .spar-card {
            position: relative;
            overflow: hidden;
        }
        
        .spar-main { 
            display: flex; 
            align-items: center; 
            gap: 32px; 
        }
        
        .score-display-column { 
            text-align: left; 
        }
        
        .score-section { 
            display: flex; 
            align-items: baseline; 
            gap: 8px; 
            margin-bottom: 8px; 
        }
        
        .score-number { 
            font-size: 72px; 
            font-weight: 700; 
            line-height: 1; 
            font-family: var(--font-display);
            color: var(--text-primary);
        }
        
        .score-total { 
            font-size: 32px; 
            font-weight: 400; 
            color: var(--text-secondary); 
        }
        
        .grade-badge {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 4px 16px;
            border-radius: 999px;
            font-size: 18px;
            font-weight: 500;
            margin-left: 16px;
        }
        
        .description { 
            font-size: 16px; 
            color: var(--text-secondary); 
            max-width: 400px; 
        }
        
        .details-column { 
            flex: 1; 
            padding-top: 16px; 
        }
        
        .spar-title {
            font-size: 16px;
            font-weight: 500;
            color: var(--text-secondary);
            margin-bottom: 12px;
            display: flex;
            align-items: center;
            gap: 8px;
        }
        
        .pulse-indicator {
            width: 100%;
            height: 32px;
            position: relative;
            overflow: hidden;
            background: rgba(0, 0, 0, 0.05);
            border-radius: 16px;
        }
        
        .pulse-bar {
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            width: 100%;
            height: 4px;
            background: linear-gradient(90deg, 
                transparent 0%, transparent 20%, currentColor 35%,
                currentColor 65%, transparent 80%, transparent 100%
            );
            animation: pulseMove var(--pulse-speed, 2s) linear infinite;
        }
        
        .pulse-bar::after {
            content: '';
            position: absolute;
            top: -6px; left: 0; right: 0; bottom: -6px;
            background: inherit;
            filter: blur(8px);
            opacity: var(--glow-intensity, 0.5);
        }
        
        @keyframes pulseMove {
            from { transform: translateX(-100%) translateY(-50%); }
            to { transform: translateX(100%) translateY(-50%); }
        }
        
        .pulse-a-plus { --pulse-color: var(--success-green); --pulse-speed: 1.2s; --glow-intensity: 0.8; }
        .pulse-a-plus .pulse-indicator { background: rgba(16, 185, 129, 0.1); }
        .pulse-a-plus .pulse-bar { color: var(--success-green); height: 4px; }
        .pulse-a-plus .grade-badge { background: #10B98120; color: var(--success-green); }
        
        .pulse-a { --pulse-color: #34D399; --pulse-speed: 1.5s; --glow-intensity: 0.7; }
        .pulse-a .pulse-indicator { background: rgba(52, 211, 153, 0.1); }
        .pulse-a .pulse-bar { color: #34D399; }
        .pulse-a .grade-badge { background: #34D39920; color: #34D399; }
        
        .pulse-b-plus { --pulse-color: #3B82F6; --pulse-speed: 1.8s; --glow-intensity: 0.6; }
        .pulse-b-plus .pulse-indicator { background: rgba(59, 130, 246, 0.1); }
        .pulse-b-plus .pulse-bar { color: #3B82F6; height: 3px; }
        .pulse-b-plus .grade-badge { background: #3B82F620; color: #3B82F6; }
        
        .pulse-b { --pulse-color: #60A5FA; --pulse-speed: 2s; --glow-intensity: 0.5; }
        .pulse-b .pulse-indicator { background: rgba(96, 165, 250, 0.1); }
        .pulse-b .pulse-bar { color: #60A5FA; }
        .pulse-b .grade-badge { background: #60A5FA20; color: #60A5FA; }
        
        .pulse-c { --pulse-color: var(--warning-yellow); --pulse-speed: 2.5s; --glow-intensity: 0.4; }
        .pulse-c .pulse-indicator { background: rgba(245, 158, 11, 0.1); }
        .pulse-c .pulse-bar { color: var(--warning-yellow); height: 3px; }
        .pulse-c .grade-badge { background: #F59E0B20; color: var(--warning-yellow); }
        
        .pulse-d { --pulse-color: #F87171; --pulse-speed: 3s; --glow-intensity: 0.3; }
        .pulse-d .pulse-indicator { background: rgba(248, 113, 113, 0.1); }
        .pulse-d .pulse-bar { color: #F87171; height: 2px; }
        .pulse-d .grade-badge { background: #F8717120; color: #F87171; }
        
        .pulse-f { --pulse-color: var(--danger-red); --pulse-speed: 3.5s; --glow-intensity: 0.2; }
        .pulse-f .pulse-indicator { background: rgba(239, 68, 68, 0.1); }
        .pulse-f .pulse-bar { color: var(--danger-red); height: 2px; }
        .pulse-f .grade-badge { background: #EF444420; color: var(--danger-red); }

        .report-info { display: flex; gap: 32px; padding: 24px 32px; background: #f8f9fa; border-radius: 12px; }
        .info-item { flex: 1; }
        .info-label { font-size: 12px; text-transform: uppercase; color: var(--text-secondary); margin-bottom: 4px; letter-spacing: 0.5px; }
        .info-value { font-size: 16px; font-weight: 500; color: var(--text-primary); }

        .insights-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 16px; }
        .insight-card {
            background: linear-gradient(180deg, #ffffff 0%, #fbfaf8 100%);
            border: 1px solid #e4ded6;
            border-radius: 22px;
            padding: 22px;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            text-align: left;
            gap: 18px;
            transition: all 0.2s ease;
            min-height: 245px;
        }
        .insight-card:hover { box-shadow: 0 18px 45px rgba(45,36,28,0.08); transform: translateY(-2px); }
        .insight-icon {
            width: 42px;
            height: 42px;
            background: #eef4ff;
            border-radius: 16px;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            color: #1a73e8;
        }
        .insight-icon.green { background: #ecf7ef; color: #188038; }
        .insight-icon.green .material-icons { color: #188038; }
        .insight-content { flex: 1; width: 100%; }
        .insight-kicker {
            font-size: 11px;
            font-weight: 700;
            letter-spacing: 0.12em;
            text-transform: uppercase;
            color: #c2410c;
            margin-bottom: 8px;
        }
        .insight-title { font-size: 20px; font-weight: 650; margin-bottom: 10px; color: var(--text-primary); }
        .insight-description { font-size: 14px; color: var(--text-secondary); line-height: 1.55; }

        .cta-card {
            display: block;
            text-decoration: none;
            background: var(--surface-color);
            border: 2px solid var(--accent-primary);
            border-radius: 12px;
            padding: 24px;
            text-align: center;
            transition: all 0.2s ease;
            cursor: pointer;
        }
        .cta-card:hover {
            background: var(--accent-active-bg);
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }
        .cta-icon {
            width: 64px;
            height: 64px;
            background: var(--accent-primary);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 16px;
        }
        .cta-icon .material-icons {
            font-size: 32px;
            color: white;
        }
        .cta-title {
            font-size: 18px;
            font-weight: 500;
            margin-bottom: 8px;
            color: var(--text-primary);
        }
        .cta-description {
            font-size: 14px;
            color: var(--text-secondary);
        }

        .bottom-cta { background: var(--accent-yellow); border-radius: 16px; padding: 48px; text-align: center; margin-top: 48px; }
        .bottom-cta h2 { font-size: 32px; font-weight: 500; margin-bottom: 16px; }
        .bottom-cta p { font-size: 18px; margin-bottom: 32px; max-width: 600px; margin-left: auto; margin-right: auto; }
        .bottom-cta .btn { font-size: 16px; padding: 14px 32px; background: var(--text-primary); color: white; border: none; }
        .bottom-cta .btn:hover { background: #000; }
        .info-circle-icon { font-size: 18px; color: var(--text-primary); cursor: pointer; vertical-align: middle; margin-left: 4px; opacity: 0.7; }
        .info-circle-icon:hover { opacity: 1; }

        .ai-badge { display: inline-flex; align-items: center; gap: 4px; background: #e8f0fe; color: var(--accent-primary); padding: 4px 12px; border-radius: 999px; font-size: 12px; font-weight: 500; cursor: pointer; }
        .ai-badge:hover { background: #d2e3fc; }

        /* Modal Styles */
        .modal-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.5); display: none; align-items: center; justify-content: center; z-index: 1000; animation: fadeIn 0.3s ease; }
        .modal-overlay.active { display: flex; }
        .modal-content { background: var(--surface-color); border-radius: 16px; padding: 24px; max-width: 700px; width: 90%; animation: slideUp 0.3s ease; }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        .modal-header { display: flex; justify-content: space-between; align-items: center; padding-bottom: 16px; border-bottom: 1px solid var(--border-color); margin-bottom: 16px; }
        .modal-title { font-size: 20px; font-weight: 500; display: flex; align-items: center; gap: 8px; }
        .modal-close { background: none; border: none; cursor: pointer; color: var(--text-secondary); }
        .specs-table { width: 100%; border-collapse: collapse; margin-top: 16px; }
        .specs-table th, .specs-table td { text-align: left; padding: 12px; border-bottom: 1px solid var(--border-color); font-size: 14px; }
        .specs-table th { font-weight: 500; color: var(--text-secondary); }
        .specs-table td:first-child { font-weight: 500; }
        .color-chip { display: inline-block; width: 16px; height: 16px; border-radius: 4px; vertical-align: middle; margin-right: 8px; border: 1px solid rgba(0,0,0,0.1); }
        .facts-list { list-style-type: '✓'; padding-left: 20px; line-height: 1.8; color: var(--text-secondary); }
        .facts-list li::marker { color: var(--success-green); }

        @media (max-width: 768px) {
            .report-header { flex-direction: column; gap: 16px; }
            .header-left, .header-right { width: 100%; justify-content: center; }
            .spar-main { flex-direction: column; }
            .insights-grid { grid-template-columns: 1fr; }
            .report-info { flex-direction: column; gap: 16px; }
            .bottom-cta { padding: 32px 24px; }
        }

        @media print {
            .report-header { display: none; }
            .bottom-cta { display: none; }
            .cta-card { display: none; }
        }
      `}</style>

      <div className="assessment-results">
        <div className="container">
          {isTestData && (
            <div style={{
              background: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '8px',
              padding: '12px 20px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span className="material-icons" style={{color: '#856404'}}>warning</span>
              <div>
                <strong>Test Mode Data</strong> - This report shows fallback content. AI analysis may have encountered an error.
                {import.meta.env.MODE === 'development' && ' Check console for details.'}
              </div>
            </div>
          )}
          <div className="title-section">
            <h1>Your ADU Feasibility Assessment</h1>
            <p>Real-time data-driven modeling of your property's ADU potential</p>
          </div>
          
          <div className={`card spar-card ${gradeInfo.class}`}>
            <div className="spar-main">
              <div className="score-display-column">
                <div className="score-section">
                  <span className="score-number">{sparScoreAi}</span>
                  <span className="score-total">/100</span>
                  <span className="grade-badge">{gradeInfo.grade}</span>
                </div>
                <p className="description">
                  {aiAnalysis?.scoreDescription || 'Your property shows strong potential for ADU development.'}
                </p>
              </div>
              <div style={{
                flex: 1,
                minWidth: '260px',
                padding: '24px',
                borderRadius: '22px',
                border: '1px solid #e4ded6',
                background: 'linear-gradient(180deg, #ffffff 0%, #fbfaf8 100%)'
              }}>
                <div style={{fontSize: '12px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#c2410c', marginBottom: '12px'}}>
                  What this means
                </div>
                <div style={{display: 'grid', gap: '12px'}}>
                  <div>
                    <div style={{fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '3px'}}>
                      Good initial fit
                    </div>
                    <div style={{fontSize: '14px', lineHeight: '1.45', color: 'var(--text-secondary)'}}>
                      Your property shows a favorable starting point for ADU planning.
                    </div>
                  </div>
                  <div>
                    <div style={{fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '3px'}}>
                      Worth a deeper read
                    </div>
                    <div style={{fontSize: '14px', lineHeight: '1.45', color: 'var(--text-secondary)'}}>
                      The full report checks zoning, economics, design fit, and next steps in more detail.
                    </div>
                  </div>
                  <div>
                    <div style={{fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '3px'}}>
                      Confirm before you act
                    </div>
                    <div style={{fontSize: '14px', lineHeight: '1.45', color: 'var(--text-secondary)'}}>
                      Local rules and site-specific details should be verified before making commitments.
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="report-info" style={{marginTop: '32px'}}>
              <div className="info-item">
                <div className="info-label">Location</div>
                <div className="info-value">
                  {(() => {
                    // Try various location formats
                    if (assessment?.addressComponents?.formatted) {
                      return assessment.addressComponents.formatted;
                    }
                    if (assessment?.addressComponents?.locality && assessment?.addressComponents?.state) {
                      return `${assessment.addressComponents.locality}, ${assessment.addressComponents.state}`;
                    }
                    if (assessment?.addressComponents?.locality) {
                      return `${assessment.addressComponents.locality}, ${answers?.zip_code || assessment?.addressComponents?.zipCode || ''}`;
                    }
                    // Fallback to just ZIP code
                    return answers?.zip_code || assessment?.addressComponents?.zipCode || 'Location not available';
                  })()}
                </div>
              </div>
              <div className="info-item">
                <div className="info-label">Report Date</div>
                <div className="info-value">{reportDate || new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
              </div>
              <div className="info-item">
                <div className="info-label">Report ID</div>
                <div className="info-value">{id.substring(0, 8).toUpperCase()}</div>
              </div>
              <div className="info-item">
                <div className="info-label">Accessible Until</div>
                <div className="info-value">
                  <span 
                    style={{color: '#1a73e8', textDecoration: 'underline', cursor: 'pointer'}}
                    onClick={() => setModalOpen('conversion')}
                  >
                    {expiryDate || new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </span>
                </div>
              </div>
            </div>
            <div style={{
              marginTop: '28px',
              padding: '22px 24px',
              borderRadius: '18px',
              border: '1px solid #e4ded6',
              background: 'linear-gradient(180deg, #ffffff 0%, #fbfaf8 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '18px',
              flexWrap: 'wrap'
            }}>
              <div>
                <div style={{fontSize: '17px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '4px'}}>
                  Get the full property-specific ADU report
                </div>
                <div style={{fontSize: '14px', lineHeight: '1.5', color: 'var(--text-secondary)'}}>
                  Turn this free snapshot into zoning, cost, ROI, and next-step guidance for your property.
                </div>
              </div>
              <button className="btn primary" onClick={handleUpgrade}>
                See Pricing and Plans
              </button>
            </div>
          </div>
          
          <div className="card">
            <div className="card-title-container">
              <h3 className="card-title">Feasibility Assessment: Summary</h3>
              <span className="ai-indicator" onClick={() => setModalOpen('ai')}>
                <span className="material-icons">auto_awesome</span>
                AI Generated
              </span>
            </div>
            <p style={{lineHeight: '1.7', color: 'var(--text-secondary)'}}>
              {aiAnalysis?.summary || 'Based on your inputs, your property shows strong potential for ADU development. The combination of your lot size and intended use case creates favorable conditions for adding an accessory dwelling unit.'}
            </p>
            <div style={{
              marginTop: '24px',
              paddingTop: '22px',
              borderTop: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '18px',
              flexWrap: 'wrap'
            }}>
              <div>
                <div style={{fontSize: '15px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '4px'}}>
                  Ready for the full property report?
                </div>
                <div style={{fontSize: '14px', lineHeight: '1.5', color: 'var(--text-secondary)'}}>
                  Compare the one-time Report and Dynamic workspace options.
                </div>
              </div>
              <button className="btn primary" onClick={handleUpgrade}>
                See Pricing and Plans
              </button>
            </div>
          </div>

          <div className="card">
            <div className="card-title-container">
              <h2 className="card-title">Key Insights</h2>
              <span className="ai-indicator">
                <span className="material-icons">auto_awesome</span>
                AI Generated
              </span>
            </div>
            <div className="insights-grid">
              <div className="insight-card">
                <div className="insight-content">
                  <div className="insight-kicker">Local rules</div>
                  <div className="insight-title">Zoning</div>
                  <div className="insight-description">
                    {aiAnalysis?.keyInsights?.zoning || 'ADU-friendly zone detected'}
                  </div>
                </div>
              </div>
              <div className="insight-card">
                <div className="insight-content">
                  <div className="insight-kicker">Build fit</div>
                  <div className="insight-title">Space</div>
                  <div className="insight-description">
                    {aiAnalysis?.keyInsights?.space || 'Multiple ADU options possible'}
                  </div>
                </div>
              </div>
              <div className="insight-card">
                <div className="insight-content">
                  <div className="insight-kicker">Market signal</div>
                  <div className="insight-title">Demand</div>
                  <div className="insight-description">
                    {aiAnalysis?.keyInsights?.demand || 'Strong rental market identified'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div style={{
            marginTop: '32px',
            borderRadius: '28px',
            border: '1px solid #dfe5ec',
            background: '#ffffff',
            boxShadow: '0 24px 70px rgba(45, 36, 28, 0.08)',
            overflow: 'hidden'
          }}>
            <div style={{padding: '30px 32px 20px', textAlign: 'center'}}>
              <div style={{fontSize: '13px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#c2410c', marginBottom: '10px'}}>
                Next step
              </div>
              <h2 style={{fontFamily: 'var(--font-display)', fontSize: '32px', fontWeight: 500, margin: 0, color: 'var(--text-primary)'}}>
                Ready for the complete ADU read?
              </h2>
              <p style={{maxWidth: '620px', margin: '12px auto 0', fontSize: '16px', lineHeight: '1.6', color: 'var(--text-secondary)'}}>
                Choose the one-time ADU Report for a full property snapshot, or Dynamic if you want an ongoing workspace with monthly credits and live updates.
              </p>
            </div>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px', padding: '0 28px 6px'}}>
              <div style={{
                background: 'linear-gradient(180deg, #ffffff 0%, #fbfaf8 100%)',
                border: '1px solid #e4ded6',
                borderRadius: '22px',
                padding: '24px 26px'
              }}>
                <div style={{fontSize: '20px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '6px'}}>
                  ADU Report
                </div>
                <div style={{fontSize: '14px', lineHeight: '1.55', color: 'var(--text-secondary)', marginBottom: '16px'}}>
                  A complete property-specific report with starter credits for follow-up AI work.
                </div>
                <div style={{fontSize: '24px', fontWeight: 700, color: 'var(--text-primary)'}}>
                  $29 <span style={{fontSize: '14px', fontWeight: 500, color: 'var(--text-secondary)'}}>one-time</span>
                </div>
              </div>
              <div style={{
                background: 'linear-gradient(180deg, #ffffff 0%, #fbfaf8 100%)',
                border: '1px solid #e4ded6',
                borderRadius: '22px',
                padding: '24px 26px'
              }}>
                <div style={{fontSize: '20px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '6px'}}>
                  ADU Dynamic
                </div>
                <div style={{fontSize: '14px', lineHeight: '1.55', color: 'var(--text-secondary)', marginBottom: '16px'}}>
                  Ongoing workspace access with monthly credits, Smart Tools, and live report updates.
                </div>
                <div style={{fontSize: '24px', fontWeight: 700, color: 'var(--text-primary)'}}>
                  $29 <span style={{fontSize: '14px', fontWeight: 500, color: 'var(--text-secondary)'}}>/month</span>
                </div>
              </div>
            </div>
            <div style={{padding: '24px 32px 30px', textAlign: 'center'}}>
              <button className="btn primary" onClick={handleUpgrade} style={{minWidth: '240px'}}>
                Compare Pricing and Plans
              </button>
            </div>
          </div>
        </div>

        {/* SPAR Scale Info Modal */}
        <div className={`modal-overlay ${modalOpen === 'spar' ? 'active' : ''}`}>
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">About the SPAR SCALE™</h2>
              <button className="modal-close" onClick={() => setModalOpen('')}>
                <span className="material-icons">close</span>
              </button>
            </div>
            <p>The SPAR SCALE™ (Strategic Personalized Action Roadmap) score is a proprietary metric that assesses the viability of your project based on dozens of factors, including zoning regulations, property characteristics, market data, and construction costs.</p>
            <p>A higher score indicates a more straightforward path to completion with lower risk and higher potential return on investment. The pulse bar provides a "living" representation of this score.</p>
            <table className="specs-table">
              <thead>
                <tr>
                  <th>Grade</th>
                  <th>Score Range</th>
                  <th>Pulse Color</th>
                  <th>Speed / Intensity</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>A+</td>
                  <td>93-100</td>
                  <td><span className="color-chip" style={{background: 'var(--success-green)'}}></span>Success Green</td>
                  <td>Fast / High</td>
                </tr>
                <tr>
                  <td>A</td>
                  <td>85-92</td>
                  <td><span className="color-chip" style={{background: 'var(--success-green)'}}></span>Success Green</td>
                  <td>Fast / High</td>
                </tr>
                <tr>
                  <td>B+ / B</td>
                  <td>70-84</td>
                  <td><span className="color-chip" style={{background: 'var(--accent-primary)'}}></span>Accent Blue</td>
                  <td>Moderate / Medium</td>
                </tr>
                <tr>
                  <td>C</td>
                  <td>60-69</td>
                  <td><span className="color-chip" style={{background: 'var(--warning-yellow)'}}></span>Warning Yellow</td>
                  <td>Slow / Low</td>
                </tr>
                <tr>
                  <td>D / F</td>
                  <td>0-59</td>
                  <td><span className="color-chip" style={{background: 'var(--danger-red)'}}></span>Danger Red</td>
                  <td>Very Slow / Faint</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        
        <div className={`modal-overlay ${modalOpen === 'ai' ? 'active' : ''}`}>
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">
                <span className="material-icons" style={{color: 'var(--accent-primary)'}}>auto_awesome</span>
                About this Summary
              </h2>
              <button className="modal-close" onClick={() => setModalOpen('')}>
                <span className="material-icons">close</span>
              </button>
            </div>
            <p style={{lineHeight: '1.6', color: 'var(--text-secondary)'}}>
              This summary was generated by ADU Lab's AI, powered by Google. It analyzes your inputs against a vast dataset of zoning laws, construction costs, and market trends to provide a personalized feasibility overview.
            </p>
            <p style={{lineHeight: '1.6', color: 'var(--text-secondary)', marginTop: '16px'}}>
              This content is for informational purposes and to help guide your decision-making process.
            </p>
          </div>
        </div>

        <div className={`modal-overlay ${modalOpen === 'facts' ? 'active' : ''}`}>
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">ADU Growth in the US</h2>
              <button className="modal-close" onClick={() => setModalOpen('')}>
                <span className="material-icons">close</span>
              </button>
            </div>
            <ul className="facts-list">
              <li>A May 2025 analysis by the Federal Reserve Bank of San Francisco suggests the number of ADUs may have surpassed 1.6 million.</li>
              <li>Between 2009–2019, growth of ADUs averaged ~8.6% per year, roughly 78,000 new ADU units being permitted per year, accelerating in recent years to over 100,000 annually.</li>
              <li>Many states relaxed regulations in the late 2010s, significantly boosting ADU permits. Over 50% of America's ADUs are located in California, Florida, Texas, and Georgia.</li>
            </ul>
          </div>
        </div>
        
        <div className={`modal-overlay ${modalOpen === 'conversion' ? 'active' : ''}`}>
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">
                <span className="material-icons" style={{color: 'var(--accent-primary)'}}>schedule</span>
                Why 90 Days?
              </h2>
              <button className="modal-close" onClick={() => setModalOpen('')}>
                <span className="material-icons">close</span>
              </button>
            </div>
            <p style={{lineHeight: '1.6', color: 'var(--text-secondary)'}}>
              We store your assessment for 90 days, giving you time to review and share it. After that, it's automatically removed from our servers to protect your privacy.
            </p>
            <p style={{lineHeight: '1.6', color: 'var(--text-secondary)', marginTop: '16px'}}>
              <strong>Want to keep it forever?</strong> When you upgrade to Report or Dynamic today, your assessment becomes permanently accessible in your account dashboard.
            </p>
            <div style={{marginTop: '24px', display: 'flex', gap: '12px'}}>
              <button 
                className="btn primary"
                onClick={() => {
                  setModalOpen('');
                  handleUpgrade();
                }}
                style={{flex: 1}}
              >
                View Upgrade Options
              </button>
              <button 
                className="btn secondary"
                onClick={() => setModalOpen('')}
                style={{flex: 1}}
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Legal Disclaimer Section */}
      <div style={{background: '#f8f9fa', padding: '48px 24px', borderTop: '1px solid #e0e0e0'}}>
        <div style={{maxWidth: '1200px', margin: '0 auto'}}>
          <p style={{fontSize: '14px', lineHeight: '1.8', color: '#5f6368', marginBottom: '24px'}}>
            <strong>Plain English Summary:</strong> {REPORTS_DISCLAIMER_PLAIN_ENGLISH_SUMMARY}
          </p>
          <div style={{borderTop: '1px solid #e0e0e0', paddingTop: '24px', marginTop: '24px'}}>
            <div style={{display: 'flex', gap: '24px', justifyContent: 'center', marginBottom: '16px'}}>
              <a href="/legal/terms" target="_blank" rel="noopener noreferrer" style={{color: '#1a73e8', textDecoration: 'none', fontSize: '14px'}}>Legal Notice and Terms of Use</a>
              <a href="/legal/service-terms" target="_blank" rel="noopener noreferrer" style={{color: '#1a73e8', textDecoration: 'none', fontSize: '14px'}}>Service Terms and Conditions</a>
              <a href="/legal/privacy" target="_blank" rel="noopener noreferrer" style={{color: '#1a73e8', textDecoration: 'none', fontSize: '14px'}}>Privacy Policy</a>
            </div>
            <p style={{textAlign: 'center', fontSize: '14px', color: '#5f6368'}}>
              For all of our legal documentation and policies, please <a href="/legal" target="_blank" rel="noopener noreferrer" style={{color: '#1a73e8', textDecoration: 'none'}}>click here</a> to see them.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default AssessmentResults;
